/**
 * Date Role - Format Pillar
 *
 * Formatação de datas para apresentação.
 *
 * @example
 * import { formatDate, formatIso, formatTimestamp } from '@attrx/role-morphic/date/format';
 *
 * formatDate('iso', '2024-12-05T19:30:00.000Z', { dateStyle: 'full', locale: 'pt-BR' });
 * // "quinta-feira, 5 de dezembro de 2024"
 */

import type { DateVariant, DateIso, DateTimestamp, DateEpoch, DateFormatOptions } from "./types";

// =============================================================================
// FORMAT ISO
// =============================================================================

/**
 * Formata ISO string para apresentação
 *
 * @example
 * formatIso('2024-12-05T19:30:00.000Z');
 * // '2024-12-05T19:30:00.000Z'
 *
 * formatIso('2024-12-05T19:30:00.000Z', { dateStyle: 'full', locale: 'pt-BR' });
 * // 'quinta-feira, 5 de dezembro de 2024'
 */
export function formatIso(value: DateIso, options?: DateFormatOptions): string {
  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  // Se não especificar opções de formatação, retorna o ISO string original
  if (
    !options ||
    (!options.dateStyle && !options.timeStyle && !options.dateOnly && !options.timeOnly)
  ) {
    return value;
  }

  return formatWithIntl(date, options);
}

// =============================================================================
// FORMAT TIMESTAMP
// =============================================================================

/**
 * Formata timestamp (ms) para apresentação
 *
 * @example
 * formatTimestamp(1733425800000);
 * // '1733425800000'
 *
 * formatTimestamp(1733425800000, { dateStyle: 'medium', locale: 'en-US' });
 * // 'Dec 5, 2024'
 */
export function formatTimestamp(value: DateTimestamp, options?: DateFormatOptions): string {
  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  // Se não especificar opções, retorna o timestamp como string
  if (
    !options ||
    (!options.dateStyle && !options.timeStyle && !options.dateOnly && !options.timeOnly)
  ) {
    return String(value);
  }

  return formatWithIntl(date, options);
}

// =============================================================================
// FORMAT EPOCH
// =============================================================================

/**
 * Formata epoch (segundos) para apresentação
 *
 * @example
 * formatEpoch(1733425800);
 * // '1733425800'
 *
 * formatEpoch(1733425800, { dateStyle: 'short', timeStyle: 'short' });
 * // '12/5/24, 7:30 PM'
 */
export function formatEpoch(value: DateEpoch, options?: DateFormatOptions): string {
  const date = new Date(value * 1000);

  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  // Se não especificar opções, retorna o epoch como string
  if (
    !options ||
    (!options.dateStyle && !options.timeStyle && !options.dateOnly && !options.timeOnly)
  ) {
    return String(value);
  }

  return formatWithIntl(date, options);
}

// =============================================================================
// MAIN FORMAT
// =============================================================================

/**
 * Formata data para apresentação
 *
 * @param variant - Variante da data ("iso", "timestamp", "epoch")
 * @param value - Valor da data
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatDate('iso', '2024-12-05T19:30:00.000Z', { dateStyle: 'full', locale: 'pt-BR' });
 * // 'quinta-feira, 5 de dezembro de 2024'
 *
 * formatDate('timestamp', 1733425800000, { dateOnly: true, dateStyle: 'medium' });
 * // 'Dec 5, 2024'
 */
export function formatDate(
  variant: DateVariant,
  value: unknown,
  options?: DateFormatOptions
): string {
  switch (variant) {
    case "iso":
      return formatIso(value as DateIso, options);
    case "timestamp":
      return formatTimestamp(value as DateTimestamp, options);
    case "epoch":
      return formatEpoch(value as DateEpoch, options);
    default:
      return "Invalid Date";
  }
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

/**
 * Formata Date usando Intl.DateTimeFormat
 */
function formatWithIntl(date: Date, options: DateFormatOptions): string {
  const locale = options.locale || "en-US";
  const formatOptions: Intl.DateTimeFormatOptions = {};

  if (options.timeZone) {
    formatOptions.timeZone = options.timeZone;
  }

  if (options.dateOnly) {
    formatOptions.dateStyle = options.dateStyle || "medium";
  } else if (options.timeOnly) {
    formatOptions.timeStyle = options.timeStyle || "medium";
  } else {
    if (options.dateStyle) formatOptions.dateStyle = options.dateStyle;
    if (options.timeStyle) formatOptions.timeStyle = options.timeStyle;
  }

  return new Intl.DateTimeFormat(locale, formatOptions).format(date);
}

// =============================================================================
// RELATIVE TIME HELPERS
// =============================================================================

/**
 * Formata diferença de tempo relativa (ex: "2 hours ago", "in 3 days")
 *
 * @param timestamp - Timestamp em milissegundos
 * @param options - Opções de formatação
 *
 * @example
 * formatRelative(Date.now() - 3600000, { locale: 'en-US' });
 * // '1 hour ago'
 */
export function formatRelative(
  timestamp: DateTimestamp,
  options?: { locale?: string; now?: DateTimestamp }
): string {
  const locale = options?.locale || "en-US";
  const now = options?.now || Date.now();
  const diff = timestamp - now;
  const absDiff = Math.abs(diff);

  // Escolhe a unidade apropriada
  let value: number;
  let unit: Intl.RelativeTimeFormatUnit;

  if (absDiff < 60000) {
    // < 1 minuto
    value = Math.round(diff / 1000);
    unit = "second";
  } else if (absDiff < 3600000) {
    // < 1 hora
    value = Math.round(diff / 60000);
    unit = "minute";
  } else if (absDiff < 86400000) {
    // < 1 dia
    value = Math.round(diff / 3600000);
    unit = "hour";
  } else if (absDiff < 2592000000) {
    // < 30 dias
    value = Math.round(diff / 86400000);
    unit = "day";
  } else if (absDiff < 31536000000) {
    // < 1 ano
    value = Math.round(diff / 2592000000);
    unit = "month";
  } else {
    value = Math.round(diff / 31536000000);
    unit = "year";
  }

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  return rtf.format(value, unit);
}
